import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import ImageGallery from "react-image-gallery";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useCallback, useMemo } from "react";
import { resetBookingFlag } from "../../dux/ui/uiBookNow";

import { themeStyles } from "../../common/styles";
import fb from "../../assets/social/facebook.svg";
import insta from "../../assets/social/instagram.svg";

import "./main.scss";

import LocationModal from "../shared/LocationModal/LocationModal";
import { getPromptData } from "../../dux/prompt";
import { useSnackbar } from "../../hooks/useSnackbar";

// Location and gallery imports
import {
  LOCATIONS,
  getUserLocation,
  findNearestLocation,
} from "../../config/locations";
import { getImagesForLocation } from "../../assets/gallery/galleryImages";
import { getLocationImages, preloadImage } from "../../utils/imageLoader";

// WhatsApp Community Popup Component
const WhatsAppCommunityPopup = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="whatsapp-popup-overlay" onClick={onClose}>
      <div className="whatsapp-popup" onClick={(e) => e.stopPropagation()}>
        <div className="whatsapp-popup-icon">
          <i className="fab fa-whatsapp"></i>
        </div>
        <h2 className="whatsapp-popup-title">Join Community</h2>
        <p className="whatsapp-popup-description">
          Connect with fellow sports enthusiasts! Join our WhatsApp community
          for updates, events, and exclusive offers.
        </p>
        <div className="whatsapp-popup-buttons">
          <button
            className="whatsapp-popup-join-btn"
            onClick={() => {
              window.open(
                "https://chat.whatsapp.com/I15T0fype7jLGdAPdisXII?mode=ac_t",
                "_blank"
              );
              onClose();
            }}
          >
            <i className="fab fa-whatsapp"></i>
            Join Group
          </button>
          <button className="whatsapp-popup-cancel-btn" onClick={onClose}>
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

const MainComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const promptVisible = useSelector((state) => state.prompt.visible);
  const { category, data } = useSelector(getPromptData);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [autoDetectedLocation, setAutoDetectedLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState("prompt"); // 'granted', 'denied', 'prompt', 'unsupported'
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [showWhatsAppPopup, setShowWhatsAppPopup] = useState(false);
  const { showError, showSuccess } = useSnackbar();

  // Detect user's location using geolocation
  const detectUserLocation = useCallback(async () => {
    setIsLoadingLocation(true);

    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        setLocationPermission("unsupported");
        setIsLoadingLocation(false);
        return;
      }

      const coords = await getUserLocation();
      setLocationPermission("granted");

      const nearest = findNearestLocation(coords.lat, coords.lng);
      setAutoDetectedLocation(nearest);

      // Auto-select nearest location if within 50km and no manual selection
      if (nearest.distance <= 50) {
        setSelectedLocation(nearest.location);
        showSuccess(
          `Auto-detected: ${
            LOCATIONS[nearest.location].displayName
          } (${Math.round(nearest.distance)}km away)`
        );
      }
      // else if (nearest.distance > 50) {
      //   showError(
      //     `Nearest location is ${Math.round(
      //       nearest.distance
      //     )}km away. Please select manually.`
      //   );
      // }
    } catch (error) {
      setLocationPermission("denied");

      if (error.code === 1) {
        showError("Location access denied. Please select location manually.");
      } else if (error.code === 2) {
        showError("Location unavailable. Please select location manually.");
      } else if (error.code === 3) {
        showError(
          "Location request timed out. Please select location manually."
        );
      } else {
        showError("Could not detect location. Please select manually.");
      }
    } finally {
      setIsLoadingLocation(false);
    }
  }, [showError, showSuccess]);

  useEffect(() => {
    dispatch(resetBookingFlag());

    // Try to get user's location on component mount
    detectUserLocation();

    // Show WhatsApp community popup only once per session
    const hasSeenPopup = localStorage.getItem("whatsappPopupShown");
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setShowWhatsAppPopup(true);
        localStorage.setItem("whatsappPopupShown", "true");
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [dispatch]);

  // Get images based on selected or auto-detected location
  const images = useMemo(() => {
    const locationToUse = selectedLocation || autoDetectedLocation?.location;

    // Try to get location-specific images first
    let images = getImagesForLocation("locationToUse");

    // If no images found, try the utility function as fallback
    if (!images || images.length === 0) {
      images = getLocationImages("locationToUse");
    }

    return images
      .filter(Boolean) // Ensures none of them are undefined
      .map((img) => ({
        original: img,
        thumbnail: img,
      }))
      .sort(() => Math.random() - 0.5); // Shuffle images for variety
  }, [selectedLocation, autoDetectedLocation?.location]);

  // Preload first gallery image for faster LCP
  useEffect(() => {
    const firstSrc = images[0]?.original;
    if (firstSrc) return preloadImage(firstSrc);
  }, [images]);

  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value);
  };

  // Render location detection status
  const renderLocationInfo = () => {
    if (locationPermission === "granted" && autoDetectedLocation) {
      return (
        <div className="location-info">
          📍 Near {LOCATIONS[autoDetectedLocation.location].displayName}(
          {Math.round(autoDetectedLocation.distance)}km away)
        </div>
      );
    }
    return null;
  };

  const handleBooking = () => {
    if (!selectedLocation) {
      showError("Please select a location before proceeding.");
      return; // Early return for better flow control
    }

    // Navigate to location landing page
    const targetPath = `/location/${selectedLocation.toLowerCase()}`;

    navigate(targetPath);
  };

  //   const handleBooking = () => {
  //   const selectedLocation = document.getElementById("location-dropdown").value;
  //   if (!selectedLocation) {
  //     dispatch(showPrompt({ category: "location", message: "Please select a location" }));
  //   } else {
  //     localStorage.setItem("selectedLocation", selectedLocation);
  //     navigate(`/book/${selectedLocation.toLowerCase()}`);
  //   }
  // };

  return (
    <>
      <div className="main-background">
        {/* Background Slider */}
        <div className="gallery-wrapper">
          <ImageGallery
            items={images}
            showFullscreenButton={false}
            showPlayButton={false}
            showThumbnails={false}
            lazyLoad={true}
            autoPlay={true}
            slideInterval={5000}
            slideDuration={800}
            showNav={false}
            showBullets={false}
            className="gallery-image"
          />
        </div>

        {/* Booking Section */}
        <div className="book-now-btn-wrapper booking-ui-wrapper">
          <h1 className="booking-heading">BRING YOUR GAME ON!</h1>

          {/* Location Detection Info */}
          {/* {renderLocationInfo()} */}

          <div className="booking-controls">
            <select
              value={selectedLocation}
              onChange={handleLocationChange}
              className={`booking-select ${
                !selectedLocation ? "placeholder" : "selected"
              }`}
              disabled={isLoadingLocation}
            >
              <option value="">
                {isLoadingLocation
                  ? "Detecting location..."
                  : "Select Location"}
              </option>
              <option value="srinagar">Sonwar Bagh, Srinagar (J&K)</option>
              {/* <option value="baramulla">SSM College, Pattan (J&K)</option> */}
              <option value="jammu">KC Sports Club, Paloura, Akhnoor Road, Jammu (J&K)</option>
            </select>

            {/* Location refresh button */}
            <Button
              onClick={detectUserLocation}
              disabled={isLoadingLocation}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
                textTransform: "unset",
                minWidth: "40px",
                padding: "8px",
                marginLeft: "10px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
                "&:disabled": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  color: "rgba(255, 255, 255, 0.5)",
                },
              }}
              style={{ height: "50px" }}
            >
              {isLoadingLocation ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <i className="fa-solid fa-location-crosshairs"></i>
              )}
            </Button>

            {/* <Button
            className="book-now-btn"
             sx={{
              backgroundColor: themeStyles.secondaryColor,
              color: themeStyles.secondaryTextColor,
              textTransform: "unset",
              '&:hover': {
                backgroundColor: 'rgba(253, 201, 11, 0.94)',
              },
            }}
            style={{ width: "160px", height: "50px", fontSize: "18px" }}
            onClick={handleBooking}
          >
            Book Now
          </Button> */}

            <Button
              className="book-now-btn"
              sx={{
                backgroundColor: themeStyles.secondaryColor,
                color: themeStyles.secondaryTextColor,
                textTransform: "unset",
                minWidth: "50px",
                padding: "10px",
                "&:hover": {
                  backgroundColor: "rgba(253, 201, 11, 0.94)",
                },
              }}
              style={{ height: "50px", fontSize: "18px" }}
              onClick={handleBooking}
            >
              <i
                className="fa-solid fa-arrow-right"
                style={{ color: "#09213c" }}
              ></i>
            </Button>
          </div>
        </div>

        {/* Social Icons */}
        <div className="social-stack">
          <div className="animated bounce">
            <a
              href="https://www.facebook.com/WHSSportsHub"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={fb}
                alt="Facebook"
                loading="lazy"
                decoding="async"
                style={{ width: "44px", marginRight: "20px" }}
              />
            </a>
            <a
              href="https://instagram.com/whssportshub?igshid=YmMyMTA2M2Y="
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={insta}
                alt="Instagram"
                loading="lazy"
                decoding="async"
                style={{ width: "48px", marginRight: "20px" }}
              />
            </a>
            <a
              href="https://wa.me/919797733171?text=Hello%20!%0D%0ACan%20you%20help%20me%20?"
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "38px",
                height: "38px",
                backgroundColor: "#25D366",
                borderRadius: "50%",
                textDecoration: "none",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                verticalAlign: "middle",
                lineHeight: "1",
              }}
            >
              <i className="fab fa-whatsapp"></i>
            </a>
          </div>
        </div>

        {/* Footer Links */}
        <div className="links">
          <div onClick={() => navigate("/privacy-policy")}>Privacy Policy</div>
          <span>|</span>
          <div onClick={() => navigate("/terms-and-conditions")}>
            Terms & Conditions
          </div>
          <span>|</span>
          <div onClick={() => navigate("/refund-policy")}>
            Cancellation/Refund Policies
          </div>
        </div>
        {promptVisible && category === "location" && (
          <LocationModal message={data.message} />
        )}

        {/* WhatsApp Community Popup */}
        <WhatsAppCommunityPopup
          isOpen={showWhatsAppPopup}
          onClose={() => setShowWhatsAppPopup(false)}
        />
      </div>
    </>
  );
};

export default MainComponent;
